/* eslint-disable no-console */
import { supabaseWorker } from '@/integrations/supabase/workerClient';

// Local non-hook logger for utility usage (avoids React hooks in plain functions)
const getLogger = (tag: string) => ({
  info: (...args: unknown[]) => console.info(`[${tag}]`, ...args),
  warn: (...args: unknown[]) => console.warn(`[${tag}]`, ...args),
  error: (...args: unknown[]) => console.error(`[${tag}]`, ...args),
});

/**
 * Получает первую активную точку аренды пользователя
 * Если точек нет, возвращает null
 */
export const getUserRentalPointId = async (userId: string): Promise<string | null> => {
  try {
    const logger = getLogger('userRentalPoint');
    logger.info('[getUserRentalPointId] Searching for rental points for user:', userId);
    const { data, error } = await supabaseWorker
      .from('rental_points')
      .select('id, name, is_active')
      .eq('user_id', userId)
      .eq('is_active', true)
      .limit(1);

    if (error) {
      const logger = getLogger('userRentalPoint');
      const msg = error instanceof Error ? error.message : String(error);
      logger.warn('[getUserRentalPointId] Failed to get user rental point:', msg);
      return null;
    }

    if (!Array.isArray(data) || data.length === 0) {
      const logger = getLogger('userRentalPoint');
      logger.info('[getUserRentalPointId] No rental points found for user');
      return null;
    }

    {
      const logger = getLogger('userRentalPoint');
      logger.info('[getUserRentalPointId] Found user rental point:', data[0]);
    }
    return (data[0] as { id?: string } | undefined)?.id ?? null;
  } catch (error) {
    const logger = getLogger('userRentalPoint');
    const msg = error instanceof Error ? error.message : String(error);
    logger.error('[getUserRentalPointId] Error getting user rental point:', msg);
    return null;
  }
};

/**
 * Получает первую доступную точку аренды в системе (fallback)
 * Используется если у пользователя нет собственных точек
 */
export const getDefaultRentalPointId = async (): Promise<string | null> => {
  try {
    const logger = getLogger('userRentalPoint');
    logger.info('[getDefaultRentalPointId] Searching for any active rental point...');
    const { data, error } = await supabaseWorker
      .from('rental_points')
      .select('id, name, is_active, user_id')
      .eq('is_active', true)
      .limit(1);

    if (error) {
      const logger = getLogger('userRentalPoint');
      const msg = error instanceof Error ? error.message : String(error);
      logger.warn('[getDefaultRentalPointId] Failed to get default rental point:', msg);
      return null;
    }

    if (!Array.isArray(data) || data.length === 0) {
      const logger = getLogger('userRentalPoint');
      logger.info('[getDefaultRentalPointId] No active rental points found in database');
      return null;
    }

    {
      const logger = getLogger('userRentalPoint');
      logger.info('[getDefaultRentalPointId] Found default rental point:', data[0]);
    }
    return (data[0] as { id?: string } | undefined)?.id ?? null;
  } catch (error) {
    const logger = getLogger('userRentalPoint');
    const msg = error instanceof Error ? error.message : String(error);
    logger.error('[getDefaultRentalPointId] Error getting default rental point:', msg);
    return null;
  }
};

/**
 * Создает тестовую точку аренды для пользователя (fallback)
 */
export const createTestRentalPoint = async (userId: string): Promise<string | null> => {
  try {
    const logger = getLogger('userRentalPoint');
    logger.info('[createTestRentalPoint] Creating test rental point for user:', userId);
    const { data, error } = await supabaseWorker
      .from('rental_points')
      .insert({
        name: 'Тестовая точка аренды',
        user_id: userId,
        is_active: true,
        address: 'Тестовый адрес',
        phone: '+7 999 123-45-67',
        email: 'test@example.com'
      })
      .select('id')
      .single();

    if (error) {
      const logger = getLogger('userRentalPoint');
      const msg = error instanceof Error ? error.message : String(error);
      logger.error('[createTestRentalPoint] Failed to create test rental point:', msg);
      return null;
    }

    {
      const logger = getLogger('userRentalPoint');
      logger.info('[createTestRentalPoint] Created test rental point:', data);
    }
    return (data as { id?: string } | null)?.id ?? null;
  } catch (error) {
    const logger = getLogger('userRentalPoint');
    const msg = error instanceof Error ? error.message : String(error);
    logger.error('[createTestRentalPoint] Error creating test rental point:', msg);
    return null;
  }
};

/**
 * Получает валидный rental_point_id для пользователя
 * Сначала пытается получить точку пользователя, затем дефолтную, затем создает тестовую
 */
export const getValidRentalPointId = async (userId: string): Promise<string | null> => {
  const logger = getLogger('userRentalPoint');
  logger.info('[getValidRentalPointId] Starting search for valid rental point for user:', userId);
  
  // Сначала пытаемся получить точку пользователя
  let rentalPointId = await getUserRentalPointId(userId);
  logger.info('[getValidRentalPointId] User rental point result:', rentalPointId);
  
  // Если у пользователя нет точек, берем первую доступную
  if (!rentalPointId) {
    logger.info('[getValidRentalPointId] No user rental point found, trying default...');
    rentalPointId = await getDefaultRentalPointId();
    logger.info('[getValidRentalPointId] Default rental point result:', rentalPointId);
  }
  
  // Если вообще нет точек аренды, создаем тестовую
  if (!rentalPointId) {
    logger.info('[getValidRentalPointId] No rental points found, creating test rental point...');
    rentalPointId = await createTestRentalPoint(userId);
    logger.info('[getValidRentalPointId] Test rental point result:', rentalPointId);
  }
  
  logger.info('[getValidRentalPointId] Final rental point ID:', rentalPointId);
  return rentalPointId;
};
